import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ADMIN_STORAGE } from '@/constants/storage';
import { adminAuthService, getApiErrorMessage } from '@/services';
import { notify } from '@/utils/notify';
import type { AdminLoginInput } from '@/services/auth/adminAuthService';
import type { PublicAdmin } from '@/types/auth';
import { clearAuthStorage, getStoredToken, persistTokens } from '@/utils/tokenSession';

interface AdminAuthContextValue {
  admin: PublicAdmin | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (input: AdminLoginInput) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

function readStoredAdmin(): PublicAdmin | null {
  const raw = localStorage.getItem(ADMIN_STORAGE.admin);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PublicAdmin;
  } catch {
    return null;
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<PublicAdmin | null>(() => readStoredAdmin());
  const [token, setToken] = useState<string | null>(() => getStoredToken('admin'));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (input: AdminLoginInput) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await adminAuthService.login(input);
      if (!res.success || !res.data) throw new Error(res.message || 'Login failed');
      persistTokens('admin', res.data.tokens.accessToken, res.data.tokens.refreshToken);
      localStorage.setItem(ADMIN_STORAGE.admin, JSON.stringify(res.data.admin));
      setAdmin(res.data.admin);
      setToken(res.data.tokens.accessToken);
    } catch (err) {
      const message = getApiErrorMessage(err, 'Invalid admin credentials. Please try again.');
      setError(message);
      notify.error(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    const accessToken = getStoredToken('admin');
    clearAuthStorage('admin');
    setAdmin(null);
    setToken(null);
    if (accessToken) {
      void adminAuthService.logout(accessToken).catch(() => undefined);
    }
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      admin,
      token,
      isAuthenticated: Boolean(admin && token),
      isLoading,
      error,
      login,
      logout,
      clearError: () => setError(null),
    }),
    [admin, token, isLoading, error, login, logout],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
